import { jsonLdScriptProps } from 'react-schemaorg'
import { useDomain } from 'context'
import { BreadcrumbJsonLd, NextSeo } from 'next-seo'
import Head from 'next/head'
import { Organization } from 'schema-dts'
import { CompanyInformation_CompanyInformationFragment } from 'generated/graphql'
import { COMPANY_INFO_META } from 'constants/metaData'
import { CompanyInfoTab, COMPANY_INFO_TABS } from '../constants/detailTabs'

interface CompanyInfoSeoProps {
  companyInformation: CompanyInformation_CompanyInformationFragment
  currentTab?: CompanyInfoTab
}

const CompanyInfoSeo = ({
  companyInformation,
  currentTab = 'overview',
}: CompanyInfoSeoProps) => {
  const { company, thumbnailImage, companyIndustry, descriptions } =
    companyInformation

  const domains = useDomain()
  const basePathname = `${domains.protocol}://${domains.linkareer}/company-info/${companyInformation.id}`
  const pathname =
    currentTab === 'overview'
      ? basePathname
      : `${basePathname}/${COMPANY_INFO_TABS[currentTab].path || currentTab}`

  const title = `${companyInformation?.company.name} 채용정보 | 자소서·합격후기·면접후기·Q&A - 링커리어`

  const logoUrl =
    thumbnailImage?.url ||
    'https://linkareer.com/static/images/linkareer_170131.png'

  const breadcrumbItems = [
    {
      position: 1,
      name: '기업정보',
      item: `https://linkareer.com/company-info`,
    },
    {
      position: 2,
      name: company.name,
      item: `https://linkareer.com/company-info/${companyInformation.id}`,
    },
    ...(currentTab && currentTab !== 'overview'
      ? [
          {
            position: 3,
            name: COMPANY_INFO_TABS[currentTab].name,
            item: `https://linkareer.com/company-info/${companyInformation.id}${
              COMPANY_INFO_TABS[currentTab].path
                ? `/${COMPANY_INFO_TABS[currentTab].path}`
                : ''
            }`,
          },
        ]
      : []),
  ]

  return (
    <>
      <NextSeo
        title={title}
        description={COMPANY_INFO_META.description}
        openGraph={{
          url: pathname,
          title: title,
          description: COMPANY_INFO_META.description,
          site_name: '링커리어',
          type: 'website',
          locale: 'ko_KR',
          images: COMPANY_INFO_META.images,
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: COMPANY_INFO_META.keywords,
          },
        ]}
        canonical={pathname}
      />

      <Head>
        <script
          {...jsonLdScriptProps<Organization>({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: company.name,
            url: company.url || pathname,
            logo: logoUrl,
            ...(company.address && {
              address: {
                '@type': 'PostalAddress',
                streetAddress: company.address,
                addressCountry: 'KR',
              },
            }),
            ...(companyIndustry && {
              knowsAbout: [
                companyIndustry.primary || '',
                companyIndustry.secondary || '',
              ]
                .filter(Boolean)
                .join(', '),
            }),
            ...(company.establishedAt && {
              foundingDate: new Date(company.establishedAt).toISOString(),
            }),
            ...(descriptions[0] && {
              description: descriptions[0].content.substring(0, 200),
            }),
          })}
        />
      </Head>

      <BreadcrumbJsonLd
        keyOverride={'company-info-breadcrumb'}
        itemListElements={breadcrumbItems}
      />
    </>
  )
}

export default CompanyInfoSeo
